import { Injectable, inject } from '@angular/core';
import { ANALYTICS_CONFIG } from './analytics.config';
import {
    AnalyticsEventMap,
    AnalyticsEventName,
    ConsentState,
    PageViewParams,
    UserProperties
} from './events.types';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private readonly config = inject(ANALYTICS_CONFIG);

    get enabled(): boolean {
        return this.config.enabled;
    }

    page(params: PageViewParams): void {
        this.track('page_view', params);
    }

    track<K extends AnalyticsEventName>(name: K, params: AnalyticsEventMap[K]): void {
        const sanitized = this.sanitize(params as unknown as Record<string, unknown>);

        if (this.config.debug) {
            // eslint-disable-next-line no-console
            console.debug('[analytics]', name, sanitized);
        }

        if (!this.config.enabled) return;
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

        window.gtag('event', name, sanitized);
    }

    identify(userId: string, props?: UserProperties): void {
        if (this.config.debug) {
            // eslint-disable-next-line no-console
            console.debug('[analytics] identify', userId, props);
        }
        if (!this.config.enabled) return;
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

        window.gtag('config', this.config.measurementId, { user_id: userId });
        if (props && Object.keys(props).length > 0) {
            window.gtag('set', 'user_properties', props as Record<string, unknown>);
        }
    }

    setConsent(state: ConsentState): void {
        if (!this.config.enabled) return;
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
        window.gtag('consent', 'update', {
            analytics_storage: state,
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied'
        });
    }

    private sanitize(params: Record<string, unknown>): Record<string, unknown> {
        const out: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) continue;
            if (typeof value === 'string') {
                out[key] = value.length > 100 ? value.slice(0, 100) : value;
            } else {
                out[key] = value;
            }
        }
        return out;
    }
}
